import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BookingPackageType, SetProductBookingContingentReservation } from '@products/models/booking.model';
import {
  CreateContingentTariffToBookingContingentReservationsMapper,
  PostCreateContingentReservations,
  RemoveContingentReservation
} from '@products/models/contingent.model';
import { ContingentBookingService } from '@products/services/contingent-booking.service';
import { ProductService } from '@products/services/product.service';
import { BOOKING_DEBOUNCE_TIME } from '@shared/tokens';
import { ContingentBookingReservationActions } from '@store/products/booking/product/contingent/contingent-booking.actions';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ContingentProductActions } from '@store/products/product.actions';
import { isEmpty } from 'lodash';
import { catchError, debounceTime, EMPTY, map, mergeMap, of, switchMap } from 'rxjs';

export const setContingentReservationDateInBooking$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContingentProductActions.setContingentBookingReservation),
      mergeMap(({ contingentReservationDates }) => {
        if (isEmpty(contingentReservationDates)) {
          return EMPTY;
        }

        return of(
          ContingentBookingReservationActions.setContingentReservationDateInBooking(contingentReservationDates)
        );
      })
    ),
  { functional: true }
);

export const setValidatedContingentReservationInBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    contingentBookingService = inject(ContingentBookingService),
    productService = inject(ProductService)
  ) =>
    actions$.pipe(
      ofType(ContingentBookingReservationActions.setContingentReservationDateInBooking),
      debounceTime(bookingDebounceTime),
      switchMap(() => {
        return productService.getSortedBookingContingentReservationsByProductSelectionListOrder$([]).pipe(
          switchMap((sortedContingentReservations) => {
            const createContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[] =
              contingentBookingService.mapSortedProductBookingContingentReservationsToCreateContingentReservationMappers(
                sortedContingentReservations
              );

            return contingentBookingService.setContingentReservations$(createContingentReservationsMappers).pipe(
              switchMap((setContingentReservations) => {
                const { orderUuid, selectedExhibition, reservations } = setContingentReservations;

                const postCreateContingentReservations: PostCreateContingentReservations = {
                  eventId: selectedExhibition.id,
                  uuid: orderUuid,
                  tickets: reservations
                };

                return contingentBookingService
                  .postCreateContingentReservations$(postCreateContingentReservations)
                  .pipe(
                    map((contingentReservationResponse) => {
                      const setProductBookingContingentReservations: SetProductBookingContingentReservation[] =
                        contingentBookingService.mapCreateContingentReservationResponseToSetProductBookingContingentReservations(
                          selectedExhibition,
                          contingentReservationResponse,
                          createContingentReservationsMappers
                        );

                      return ContingentBookingReservationActions.setValidatedContingentReservationInBooking(
                        setProductBookingContingentReservations
                      );
                    })
                  );
              }),
              catchError(() => of(ContingentBookingReservationActions.setValidatedContingentReservationInBooking([])))
            );
          })
        );
      })
    ),
  { functional: true }
);

export const removeAndRevalidateContingentReservationsBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    contingentBookingService = inject(ContingentBookingService),
    productService = inject(ProductService)
  ) =>
    actions$.pipe(
      ofType(ContingentProductActions.removeContingentBookingReservation),
      debounceTime(bookingDebounceTime),
      switchMap(({ removeContingentReservations }) => {
        if (isEmpty(removeContingentReservations)) {
          return EMPTY;
        }

        return productService
          .getSortedBookingContingentReservationsByProductSelectionListOrder$(removeContingentReservations)
          .pipe(
            switchMap((sortedContingentReservations) => {
              const createContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[] =
                contingentBookingService.mapSortedProductBookingContingentReservationsToCreateContingentReservationMappers(
                  sortedContingentReservations
                );

              return contingentBookingService.setContingentReservations$(createContingentReservationsMappers).pipe(
                switchMap((setContingentReservations) => {
                  const { selectedExhibition, orderUuid, reservations } = setContingentReservations;

                  if (!reservations.length) {
                    return contingentBookingService
                      .postReleaseContingentReservations$(setContingentReservations.orderUuid)
                      .pipe(
                        map(() => ContingentBookingReservationActions.setValidatedContingentReservationInBooking([]))
                      );
                  }

                  const postCreateContingentReservations: PostCreateContingentReservations = {
                    eventId: selectedExhibition.id,
                    uuid: orderUuid,
                    tickets: reservations
                  };

                  return contingentBookingService
                    .postCreateContingentReservations$(postCreateContingentReservations)
                    .pipe(
                      map((contingentReservationResponse) => {
                        const setProductBookingContingentReservations: SetProductBookingContingentReservation[] =
                          contingentBookingService.mapCreateContingentReservationResponseToSetProductBookingContingentReservations(
                            selectedExhibition,
                            contingentReservationResponse,
                            createContingentReservationsMappers
                          );

                        return ContingentBookingReservationActions.setValidatedContingentReservationInBooking(
                          setProductBookingContingentReservations
                        );
                      })
                    );
                }),
                catchError(() => of(ContingentBookingReservationActions.setValidatedContingentReservationInBooking([])))
              );
            })
          );
      })
    ),
  { functional: true }
);

export const revalidateContingentReservationsBookingOnPackageRemoval$ = createEffect(
  (actions$ = inject(Actions), contingentBookingService = inject(ContingentBookingService)) =>
    actions$.pipe(
      ofType(PackageProductBookingActions.removePackageFromBookingList),
      map(({ bookingProductTypes }) => {
        const removeContingentReservations: RemoveContingentReservation[] =
          contingentBookingService.mapRemovedBookingPackageTypesToRemoveContingentReservations(
            bookingProductTypes as BookingPackageType[]
          );

        return ContingentProductActions.removeContingentBookingReservation(removeContingentReservations);
      })
    ),
  { functional: true }
);
